import { Button, Modal, PreloaderCircular } from '@shared/ui/kit';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { XmarkIcon } from '@shared/assets/icons';
import { SendChannel, SendContentType, TicketDeliveryMethod } from '@pages/event/api/useSendMessages';
import type { UpdateSettingInput } from './api/updateSetting';
import { formatLocalDateToUtc } from '@shared/libs/format-local-date';
import type { SendMessagesInput } from '@pages/event/api/useSendMessages';
import { useToast } from '@shared/hooks/useToast';
import { ModalStatus } from '@features/sms-email-status';
import { useFetchMessagesStats } from '@pages/event/api/useFetchMessagesStats';
import { StepInvitation } from '@features/step-invitation-modal';
import { InvitationType } from './model/types';
import { StepTicket } from '@features/step-ticket-modal';
import { StepSummary } from '@features/step-summary-modal';
import { calculateInvitationCounts, calculateTicketCounts } from './lib/calculation-utils';
import { getHeaderTitle, getStepInfo, INVITATION_STEPS } from './lib/step-utils';
import useGetSettingsByEventId from '@features/api/use-get-settings';
import { calcSmsParts20_70 } from '@shared/libs/sms-utils';
import { mergeStylesForMail } from '@features/modal-invitation/lib/mergeStylesForMail';
import { getTextFromHtml } from '@features/modal-invitation/lib/getTextFromHtml';
import { isDatePassedDateOnly } from '@shared/libs/date-validation';

interface ModalinvitationProps {
  usersSMS: { users: number[]; tickets: number[]; isAdmin: boolean };
  eventId?: number;
  eventDateEnd?: string;
  messageAttempt?: number;
  totalInvites?: number;
  sendMessages: (input: SendMessagesInput) => Promise<void>;
  active: boolean;
  resetAttempts: () => void;
  closeHandle: () => void;
  onOpenSettings?: () => void;
  refetchEvent?: () => void;
}

const invitationTypes: InvitationType[] = [
  { id: 1, label: 'СМС', value: TicketDeliveryMethod.SMS },
  { id: 2, label: 'Email', value: TicketDeliveryMethod.Email },
  { id: 3, label: 'СМС и Email', value: TicketDeliveryMethod.Both },
];

const INVITATION_DEFAULT_TIME = 'T23:59:59';
const TICKET_DEFAULT_TIME = 'T11:00:00';

export const ModalInvitations: FC<ModalinvitationProps> = ({
  usersSMS,
  eventId,
  eventDateEnd,
  active,
  closeHandle,
  sendMessages,
  messageAttempt,
  resetAttempts,
  totalInvites = 0,
  onOpenSettings,
  refetchEvent,
}) => {
  const [message, setMessage] = useState('');
  const [messageErrors, setMessageErrors] = useState<string | false>(false);
  const [selectedType, setSelectedType] = useState<InvitationType | null>(null);
  const [selectedTypeError, setSelectedTypeError] = useState<string | false>(false);
  const [ticketDeliveryMethod, setTicketDeliveryMethod] = useState<TicketDeliveryMethod | null>(null);
  const [ticketDeliveryMethodError, setTicketDeliveryMethodError] = useState<string | false>(false);
  const [ticketSmsMessage, setTicketSmsMessage] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [ticketMessageError, setTicketMessageError] = useState<string | false>(false);
  const [step, setStep] = useState<number>(INVITATION_STEPS.INVITATION_SETUP);
  const [isTouched, setIsTouched] = useState(false);
  const [isTypeTouched, setIsTypeTouched] = useState(false);
  const [isTicketMessageTouched, setIsTicketMessageTouched] = useState(false);
  const [isTicketDeliveryMethodTouched, setIsTicketDeliveryMethodTouched] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [showErrorUsers, setShowErrorUsers] = useState(false);
  const [errorChannel, setErrorChannel] = useState<'sms' | 'email' | 'vk' | null>(null);
  const [isStatusVisible, setIsStatusVisible] = useState(false);
  const [invitationExpiresAt, setInvitationExpiresAt] = useState<Date | null>(null);
  const [invitationExpiresAtError, setInvitationExpiresAtError] = useState<string | false>(false);
  const [sendTicketAfterAcceptAt, setSendTicketAfterAcceptAt] = useState<Date | null>(null);
  const [sendTicketDateError, setSendTicketDateError] = useState<string | false>(false);
  const [isInvitationDateTouched, setIsInvitationDateTouched] = useState(false);
  const [isTicketDateTouched, setIsTicketDateTouched] = useState(false);
  const [inviteAcceptionInfo, setInviteAcceptionInfo] = useState('');
  const [isSkipTicketSendChecked, setIsSkipTicketSendChecked] = useState(false);

  const { showToast } = useToast();
  const { data: eventSettings, refetch: refetchEventSettings } = useGetSettingsByEventId(eventId || 0);

  // повторная отравка или нет
  const isRepeatSending = totalInvites > 0;
  const isInitialInvitationDatePassed = useMemo(
    () => isDatePassedDateOnly(eventSettings?.invitationExpiresAt),
    [eventSettings?.invitationExpiresAt]
  );
  const isInitialTicketSendDatePassed = useMemo(
    () => isDatePassedDateOnly(eventSettings?.sendTicketAfterAcceptAt),
    [eventSettings?.sendTicketAfterAcceptAt]
  );
  const isRepeatFlowWithUnlockedInvitationSettings =
    isRepeatSending && isInitialInvitationDatePassed && !isInitialTicketSendDatePassed;
  const isInvitationSettingsLocked = isRepeatSending && !isRepeatFlowWithUnlockedInvitationSettings;

  const {
    messagesStats,
    error: messagesStatsError,
    refetch: refetchStats,
    fetching: isLoadingMessagesStats,
  } = useFetchMessagesStats(
    {
      eventId: eventId ?? 0,
      attempt: messageAttempt ?? null,
      contentType: SendContentType.Invite,
    },
    !active || !eventId || messageAttempt === undefined
  );

  useEffect(() => {
    if (!active || !hasSent || !eventId || messageAttempt == null) {
      setIsPolling(false);
      return;
    }

    if (messagesStats?.isSent) {
      setIsPolling(false);
      return;
    }

    let cancelled = false;
    setIsPolling(true);

    const pollStats = async () => {
      if (cancelled) return;

      try {
        await refetchStats();
      } catch (error) {
        console.error('Polling error:', error);
        setIsPolling(false);
        return;
      }

      if (!cancelled && active && hasSent) {
        setTimeout(pollStats, 5000);
      } else {
        setIsPolling(false);
      }
    };

    pollStats();

    return () => {
      cancelled = true;
      setIsPolling(false);
    };
  }, [active, hasSent, eventId, messageAttempt, messagesStats?.isSent]);

  const resetModal = useCallback(() => {
    setStep(INVITATION_STEPS.INVITATION_SETUP);
    setSelectedType(null);
    setMessage('');
    setTicketSmsMessage('');
    setTicketMessage('');
    setTicketDeliveryMethod(null);
    setMessageErrors(false);
    setSelectedTypeError(false);
    setTicketDeliveryMethodError(false);
    setTicketMessageError(false);
    setIsTouched(false);
    setIsTypeTouched(false);
    setIsTicketMessageTouched(false);
    setIsTicketDeliveryMethodTouched(false);
    setIsStatusVisible(false);
    setShowErrorUsers(false);
    setErrorChannel(null);
    setInvitationExpiresAt(null);
    setInvitationExpiresAtError(false);
    setIsInvitationDateTouched(false);
    setSendTicketAfterAcceptAt(null);
    setSendTicketDateError(false);
    setIsTicketDateTouched(false);
    setInviteAcceptionInfo('');
    setIsSkipTicketSendChecked(false);
  }, []);

  useEffect(() => {
    if (!active) {
      resetModal();
    }
  }, [active, resetModal]);

  useEffect(() => {
    if (!(active && eventSettings)) return;

    if (eventSettings.sendTicketAfterAcceptAt) {
      setSendTicketAfterAcceptAt(new Date(eventSettings.sendTicketAfterAcceptAt));
    }

    const smsMessage = eventSettings.ticketDeliveryMessage || '';
    const message = eventSettings.emailTicketDeliveryMessage || eventSettings.ticketDeliveryMessage || '';
    if (totalInvites > 0) {
      setTicketMessage(message);
      setTicketSmsMessage(smsMessage);
    }

    if (eventSettings.ticketDeliveryMethod?.length) {
      const methods = eventSettings.ticketDeliveryMethod;
      const hasSms = methods.includes(TicketDeliveryMethod.SMS);
      const hasEmail = methods.includes(TicketDeliveryMethod.Email);
      let legacyMethod: TicketDeliveryMethod = TicketDeliveryMethod.SMS;
      if (hasSms && hasEmail) {
        legacyMethod = TicketDeliveryMethod.Both;
      } else if (hasEmail) {
        legacyMethod = TicketDeliveryMethod.Email;
      }
      setTicketDeliveryMethod(legacyMethod);
    }

    if (eventSettings.invitationExpiresAt) {
      setInvitationExpiresAt(new Date(eventSettings.invitationExpiresAt));
    }

    if (eventSettings.inviteAcceptionInfo) {
      setInviteAcceptionInfo(eventSettings.inviteAcceptionInfo);
    }

    if (totalInvites > 0) {
      setIsSkipTicketSendChecked(eventSettings.skipTicketsOnAccept !== false);
    }
  }, [active, eventSettings, totalInvites]);

  useEffect(() => {
    if (!active || !eventId) return;
    refetchEventSettings?.();
  }, [active, eventId, refetchEventSettings]);

  const handleClose = () => {
    resetModal();
    resetAttempts();
    closeHandle();
    handleBack();
    refetchEvent?.();
  };

  const validateMessage = useCallback(() => {
    if (!message.trim()) {
      setMessageErrors('Введите текст приглашения');
      return false;
    }
    setMessageErrors(false);
    return true;
  }, [message]);

  const validateTicketDate = useCallback(() => {
    if (isRepeatSending) {
      return true;
    }

    if (!sendTicketAfterAcceptAt) {
      setSendTicketDateError(false);
      return true;
    }

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const selectedDate = new Date(sendTicketAfterAcceptAt);

    if (selectedDate < currentDate) {
      setSendTicketDateError('Дата раньше текущей');
      return false;
    }

    setSendTicketDateError(false);
    return true;
  }, [sendTicketAfterAcceptAt, isRepeatSending]);

  const validateTicketData = useCallback(() => {
    if (isSkipTicketSendChecked) {
      setTicketDeliveryMethodError(false);
      setTicketMessageError(false);
      setSendTicketDateError(false);
      return true;
    }

    let isValid = true;

    setSendTicketDateError(false);

    if (!ticketDeliveryMethod) {
      setTicketDeliveryMethodError('Обязательное поле');
      isValid = false;
    } else {
      setTicketDeliveryMethodError(false);
    }

    if (!ticketMessage.trim()) {
      setTicketMessageError('Введите текст билета');
      isValid = false;
    } else {
      setTicketMessageError(false);
    }

    return isValid;
  }, [
    isSkipTicketSendChecked,
    ticketDeliveryMethod,
    ticketMessage,
    sendTicketAfterAcceptAt,
    isRepeatSending,
    validateTicketDate,
  ]);

  const validateSelectedType = useCallback(() => {
    if (!selectedType) {
      setSelectedTypeError('Обязательное поле');
      return false;
    }
    setSelectedTypeError(false);
    return true;
  }, [selectedType]);

  const validateInvitationDate = useCallback(() => {
    if (isInvitationSettingsLocked) {
      setInvitationExpiresAtError(false);
      return true;
    }

    if (!invitationExpiresAt) {
      setInvitationExpiresAtError('Обязательное поле');
      return false;
    }

    // Сброс времени для корректного сранвеения только по дням
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const selectedDate = new Date(invitationExpiresAt);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate < currentDate) {
      setInvitationExpiresAtError('Дата раньше текущей');
      return false;
    }

    if (eventDateEnd) {
      const eventEndDate = new Date(eventDateEnd);
      eventEndDate.setHours(0, 0, 0, 0);
      if (selectedDate > eventEndDate) {
        setInvitationExpiresAtError('Срок действия приглашения позже даты проведения мероприятия');
        return false;
      }
    }

    setInvitationExpiresAtError(false);
    return true;
  }, [invitationExpiresAt, eventDateEnd, isInvitationSettingsLocked]);

  useEffect(() => {
    if (isTouched) {
      validateMessage();
    }
  }, [message, isTouched, validateMessage]);

  useEffect(() => {
    if (isTypeTouched) {
      validateSelectedType();
    }
  }, [selectedType, isTypeTouched, validateSelectedType]);

  useEffect(() => {
    if (isTicketMessageTouched) {
      if (!ticketMessage.trim()) {
        setTicketMessageError('Введите текст билета');
      } else {
        setTicketMessageError(false);
      }
    }
  }, [ticketMessage, isTicketMessageTouched]);

  useEffect(() => {
    if (isTicketDeliveryMethodTouched) {
      if (!ticketDeliveryMethod) {
        setTicketDeliveryMethodError('Обязательное поле');
      } else {
        setTicketDeliveryMethodError(false);
      }
    }
  }, [ticketDeliveryMethod, isTicketDeliveryMethodTouched]);

  useEffect(() => {
    if (!isInvitationDateTouched) return;

    validateInvitationDate();
  }, [invitationExpiresAt, isInvitationDateTouched, validateInvitationDate]);

  useEffect(() => {
    if (!isTicketDateTouched) return;

    validateTicketDate();
  }, [sendTicketAfterAcceptAt, isTicketDateTouched, validateTicketDate]);

  const handleTicketDeliveryChange = (value: InvitationType[]) => {
    const method = value[0].value;
    setTicketDeliveryMethod(method);
    setTicketDeliveryMethodError(false);
    setIsTicketDeliveryMethodTouched(true);
    setTicketMessage(method === TicketDeliveryMethod.SMS ? getTextFromHtml(ticketMessage) : ticketMessage);
    setIsTicketMessageTouched(false);
  };

  const handleTicketMessageChange = (value: string) => {
    setTicketMessage(value);
    setIsTicketMessageTouched(true);
  };

  const handleDropdownChange = (v: InvitationType[] | InvitationType[keyof InvitationType][]) => {
    if (!active) return;

    if (v.length === 0) {
      setSelectedType(null);
      setIsTypeTouched(true);
      return;
    }

    const first = v[0];
    setMessage('');
    setIsTouched(false);
    if (typeof first === 'object' && first !== null && 'value' in first) {
      setSelectedType(first as InvitationType);
      setSelectedTypeError(false);
      setIsTypeTouched(true);
      setMessage(first.value === TicketDeliveryMethod.SMS ? getTextFromHtml(message) : message);
      return;
    }
    const found = invitationTypes.find((o) => o.value === String(first));

    if (found) {
      setSelectedType(found);
      setSelectedTypeError(false);
      setMessage(found.value === TicketDeliveryMethod.SMS ? getTextFromHtml(message) : message);
    }
    setIsTypeTouched(true);
  };

  const changeHandler = (value: string) => {
    setMessage(value);
    setIsTouched(true);
  };

  const handleInviteAcceptionInfoChange = (value: string) => {
    setInviteAcceptionInfo(value);
  };

  const handleNext = () => {
    if (step === INVITATION_STEPS.INVITATION_SETUP) {
      setIsTouched(true);
      setIsTypeTouched(true);
      setIsInvitationDateTouched(true);

      const isMessageValid = validateMessage();
      const isTypeValid = validateSelectedType();
      const isDateValid = validateInvitationDate();

      if (!isMessageValid || !isTypeValid || !isDateValid) return;
    } else if (step === INVITATION_STEPS.TICKET_SETUP) {
      setIsTicketMessageTouched(true);
      setIsTicketDeliveryMethodTouched(true);
      setIsTicketDateTouched(true);

      const isTicketDataValid = validateTicketData();
      const isTicketDateValid = validateTicketDate();
      if (!isTicketDataValid || !isTicketDateValid) return;
    }

    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);

    if (step === INVITATION_STEPS.SUMMARY) {
      // При повторной отправке не очищаем дтау отправки билета
      if (!isRepeatSending) {
        setSendTicketAfterAcceptAt(null);
      }
    } else if (step === INVITATION_STEPS.TICKET_SETUP) {
      // При потворной отправке приглашения не очищаем дату окончания действия приглашения
      if (!isRepeatSending) {
        setInvitationExpiresAt(null);
      }
    }
  };

  const handleSendInvitations = async () => {
    setIsTouched(true);
    setIsTypeTouched(true);
    setIsTicketMessageTouched(true);
    setIsTicketDeliveryMethodTouched(true);
    setIsInvitationDateTouched(true);
    setIsTicketDateTouched(true);

    const isMessageValid = validateMessage();
    const isTypeValid = validateSelectedType();
    const isTicketDataValid = validateTicketData();
    const isDateValid = validateInvitationDate();
    const isTicketDateValid = validateTicketDate();

    if (!isMessageValid || !isTypeValid || !isTicketDataValid || !isDateValid || !isTicketDateValid) return;
    if (!selectedType) {
      showToast('Выберите тип отправки', 'error');
      return;
    }
    // логика отправки
    try {
      resetAttempts();
      setHasSent(true);

      let channel: SendChannel[];

      switch (selectedType.value) {
        case TicketDeliveryMethod.SMS:
          channel = [SendChannel.SMS];
          break;
        case TicketDeliveryMethod.Email:
          channel = [SendChannel.Email];
          break;
        case TicketDeliveryMethod.Both:
          channel = [SendChannel.SMS, SendChannel.Email];
          break;
        default:
          showToast('Выберите тип отправки', 'error');
          return;
      }

      // Преобразуем локальную дату в UTC формат для отправки на сервер
      const invitationExpiresAtUtc =
        (invitationExpiresAt && formatLocalDateToUtc(invitationExpiresAt, INVITATION_DEFAULT_TIME)) ?? null;
      const sendTicketAfterAcceptAtUtc =
        (sendTicketAfterAcceptAt && formatLocalDateToUtc(sendTicketAfterAcceptAt, TICKET_DEFAULT_TIME)) ?? null;

      const isTicketMethodSms = ticketDeliveryMethod === TicketDeliveryMethod.SMS;
      const effectiveTicketDeliveryMethod = isSkipTicketSendChecked ? null : ticketDeliveryMethod;
      const skipTicketsOnAccept = isRepeatSending
        ? eventSettings?.skipTicketsOnAccept !== false
        : isSkipTicketSendChecked;
      let ticketDeliveryMessage = '';
      let emailTicketDeliveryMessage = '';

      if (!isSkipTicketSendChecked) {
        ticketDeliveryMessage = ticketSmsMessage || getTextFromHtml(ticketMessage).trim();
      }

      if (!isSkipTicketSendChecked && !isTicketMethodSms) {
        emailTicketDeliveryMessage = mergeStylesForMail(ticketMessage);
      }

      let legacyTicketDeliveryMethods: TicketDeliveryMethod[] | null = null;
      if (effectiveTicketDeliveryMethod) {
        legacyTicketDeliveryMethods =
          effectiveTicketDeliveryMethod === TicketDeliveryMethod.Both
            ? [TicketDeliveryMethod.SMS, TicketDeliveryMethod.Email]
            : [effectiveTicketDeliveryMethod];
      }

      const eventSettingsPayload: UpdateSettingInput = {
        invitationExpiresAt: invitationExpiresAtUtc,
        skipTicketsOnAccept,
        ticketDeliveryMethod: legacyTicketDeliveryMethods,
        ticketDeliveryMessage,
        emailTicketDeliveryMessage,
        sendTicketAfterAcceptAt: isSkipTicketSendChecked ? null : sendTicketAfterAcceptAtUtc,
        inviteAcceptionInfo,
      };

      const legacyHasEmail = channel.includes(SendChannel.Email);
      const legacyHasSms = channel.includes(SendChannel.SMS);

      await sendMessages({
        ticketIds: usersSMS.tickets,
        messageTemplate: !legacyHasSms ? '' : getTextFromHtml(message).trim(),
        emailMessageTemplate: !legacyHasEmail ? '' : mergeStylesForMail(message),
        contentType: SendContentType.Invite,
        channel,
        eventSettings: eventSettingsPayload,
      });
      refetchEventSettings?.();
      setIsStatusVisible(true);
    } catch (error) {
      showToast('Произошла ошибка при отправке приглашений', 'error');
    } finally {
      setHasSent(false);
    }
  };

  const messageParts = calcSmsParts20_70(getTextFromHtml(message));
  const ticketMessageParts = calcSmsParts20_70(ticketSmsMessage || getTextFromHtml(ticketMessage));

  // Расчет количества сообщений
  const invitationCounts = calculateInvitationCounts(
    selectedType?.value ?? null,
    usersSMS.tickets.length,
    messageParts
  );
  const ticketCounts = calculateTicketCounts(
    isSkipTicketSendChecked ? null : ticketDeliveryMethod,
    usersSMS.tickets.length,
    ticketMessageParts
  );

  // Общее количество
  const totalEmail = invitationCounts.email + ticketCounts.email;
  const totalSMS = invitationCounts.sms + ticketCounts.sms;
  const totalSMSCost = totalSMS * 5;
  const shouldShowTicketsSection = !isSkipTicketSendChecked && !!ticketDeliveryMethod;

  const isWaitingForTicketSend = hasSent || isPolling || messagesStats?.isSent === false || !!messagesStatsError;

  useEffect(() => {
    if (!isWaitingForTicketSend || !messageAttempt) return;

    const timer = setTimeout(() => {
      showToast('Отправка приглашений началась, статус можно отслеживать в разделе мероприятия', 'success', {
        duration: 60000,
        closable: true,
      });

      closeHandle();
    }, 30000);

    return () => clearTimeout(timer);
  }, [isWaitingForTicketSend, messageAttempt]);

  const getHeaderStyle = (step: number, isWaitingForTicketSend: boolean) => {
    if (isWaitingForTicketSend) {
      return styles.headerSending;
    }
    switch (step) {
      case INVITATION_STEPS.INVITATION_SETUP:
        return styles.headerInvitationSetup;
      case INVITATION_STEPS.TICKET_SETUP:
        return styles.headerTicketSetup;
      case INVITATION_STEPS.SUMMARY:
        return styles.headerSummary;
      default:
        return styles.header;
    }
  };

  const getContent = () => {
    const stepInfo = getStepInfo(step);
    if (isWaitingForTicketSend) {
      return (
        <>
          <div {...stylex.props(styles.loader)}>
            <PreloaderCircular variant="secondary" size="large" />

            <p {...stylex.props(styles.text)}>
              Мы отправляем Приглашения.
              <br /> Процесс может занять некоторое время
            </p>
          </div>
          <div {...stylex.props(styles.btn)}>
            <Button
              size="small"
              variant="secondary"
              settings={{ padding: '8px 20px', fontSize: '14px' }}
              onClick={handleClose}
            >
              Закрыть
            </Button>
          </div>
        </>
      );
    }

    if (isStatusVisible) {
      return (
        <ModalStatus
          messagesStats={messagesStats}
          closeHandle={handleClose}
          showErrorUsers={showErrorUsers}
          setShowErrorUsers={setShowErrorUsers}
          errorChannel={errorChannel}
          setErrorChannel={setErrorChannel}
          isLoading={isLoadingMessagesStats}
        />
      );
    }
    if (step === INVITATION_STEPS.INVITATION_SETUP) {
      return (
        <StepInvitation
          selectedType={selectedType}
          selectedTypeError={selectedTypeError}
          message={message}
          messageErrors={messageErrors}
          messageParts={messageParts}
          handleDropdownChange={handleDropdownChange}
          changeHandler={changeHandler}
          handleNext={handleNext}
          handleClose={handleClose}
          stepInfo={stepInfo}
          invitationTypes={invitationTypes}
          invitationExpiresAt={invitationExpiresAt}
          invitationExpiresAtError={invitationExpiresAtError}
          eventDateEnd={eventDateEnd}
          previousInvitationExpiresAt={eventSettings?.invitationExpiresAt || null}
          isInvitationSettingsLocked={isInvitationSettingsLocked}
          onInvitationDateChange={(date) => {
            setInvitationExpiresAt(date);
            setIsInvitationDateTouched(true);
          }}
          onOpenSettings={onOpenSettings}
          inviteAcceptionInfo={inviteAcceptionInfo}
          onChangeInviteAcceptionInfo={handleInviteAcceptionInfoChange}
        />
      );
    } else if (step === INVITATION_STEPS.TICKET_SETUP) {
      return (
        <StepTicket
          ticketDeliveryMethod={ticketDeliveryMethod}
          ticketDeliveryMethodError={ticketDeliveryMethodError}
          ticketMessage={ticketMessage}
          ticketMessageError={ticketMessageError}
          ticketMessageParts={ticketMessageParts}
          handleTicketDeliveryChange={handleTicketDeliveryChange}
          handleTicketMessageChange={handleTicketMessageChange}
          handleNext={handleNext}
          stepInfo={stepInfo}
          invitationTypes={invitationTypes}
          handleBack={handleBack}
          sendTicketAfterAcceptAt={sendTicketAfterAcceptAt}
          onTicketDateChange={(date) => {
            setSendTicketAfterAcceptAt(date);
            setIsTicketDateTouched(true);
          }}
          eventSettings={eventSettings}
          invitationExpiresAt={invitationExpiresAt}
          eventDateEnd={eventDateEnd}
          sendTicketDateError={sendTicketDateError}
          isTicketDateTouched={isTicketDateTouched}
          onOpenSettings={onOpenSettings}
          handleClose={handleClose}
          totalInvites={totalInvites}
          isSkipTicketSendChecked={isSkipTicketSendChecked}
          onSkipTicketSendChange={setIsSkipTicketSendChecked}
        />
      );
    } else {
      return (
        <StepSummary
          usersSMS={usersSMS}
          invitationCounts={invitationCounts}
          ticketCounts={ticketCounts}
          shouldShowTicketsSection={shouldShowTicketsSection}
          totalEmail={totalEmail}
          totalSMS={totalSMS}
          totalSMSCost={totalSMSCost}
          handleSendInvitations={handleSendInvitations}
          handleBack={handleBack}
          stepInfo={stepInfo}
          invitationExpiresAt={invitationExpiresAt}
          sendTicketAfterAcceptAt={sendTicketAfterAcceptAt}
        />
      );
    }
  };

  return (
    <>
      {createPortal(
        <Modal
          active={active}
          settingsContainer={{ height: 'auto', minWidth: '480px', padding: '0px', overflow: 'visible' }}
          settingsHeader={{ padding: '0px' }}
          settingsFooter={{ padding: '0px' }}
          settings={{ overflow: 'visible' }}
        >
          <div {...stylex.props(styles.container, isWaitingForTicketSend && styles.containerLoading)}>
            <div {...stylex.props(styles.headerContainer)}>
              <p {...stylex.props(getHeaderStyle(step, isWaitingForTicketSend))}>
                {isWaitingForTicketSend ? 'Отправка' : getHeaderTitle(step)}
              </p>
              <XmarkIcon onClick={handleClose} size="24" {...stylex.props(styles.xMarkIcon)} />
            </div>

            {getContent()}
          </div>
        </Modal>,
        document.getElementById('modal') as Element
      )}
    </>
  );
};
